import { useRef, useLayoutEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Tooltip, Button, CircularProgress, Link, Fab, Badge } from '@mui/material';
import { UserAvatar } from '@/components/common';
import DoneIcon from '@mui/icons-material/Done';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { format, isToday, isYesterday } from 'date-fns';
import type { Message } from '@/types';
import { dateFnsLocale } from '@/utils/locale';

interface MessageThreadProps {
  messages: Message[];
  customerName: string;
  typingIndicator?: { conversationId: string; customerName: string } | null;
  hasOlder?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
}

/** Distance from the bottom (px) within which the thread still counts as "at the latest message". */
const NEAR_BOTTOM_PX = 80;

function isNearBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
}

function formatMessageDate(dateStr: string, t: (key: string) => string): string {
  const date = new Date(dateStr);
  if (isToday(date)) {
    return t('today');
  }
  if (isYesterday(date)) {
    return t('yesterday');
  }
  return format(date, 'd. MMMM', { locale: dateFnsLocale() });
}

function formatMessageTime(dateStr: string): string {
  return format(new Date(dateStr), 'HH:mm');
}

function MessageStatusIcon({ status, errorReason }: { status: Message['status']; errorReason?: string }) {
  if (status === 'failed') {
    return (
      <Tooltip title={errorReason || ''} arrow>
        <ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />
      </Tooltip>
    );
  }
  if (status === 'read') {
    // WhatsApp read-receipt blue tick, fixed domain color
    return <DoneAllIcon sx={{ fontSize: 14, color: '#53bdeb' /* design-allow-hex */ }} />;
  }
  if (status === 'delivered') {
    return <DoneAllIcon sx={{ fontSize: 14, color: 'text.disabled' }} />;
  }
  if (status === 'sent') {
    return <DoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />;
  }
  return null;
}

export function MessageThread({
  messages,
  customerName,
  typingIndicator,
  hasOlder = false,
  loadingOlder = false,
  onLoadOlder,
}: MessageThreadProps) {
  const { t } = useTranslation('messages');
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pendingRestoreRef = useRef<number | null>(null);
  // Whether the reader is at the newest message. Updated from scroll events, so
  // a re-render (new message, status update, typing indicator) never pulls a
  // reader who scrolled back in history down to the bottom (#4052).
  const stickToBottomRef = useRef(true);
  const prevMessagesRef = useRef<Message[]>([]);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [hasUnseenNewer, setHasUnseenNewer] = useState(false);

  const scrollToBottom = useCallback((behavior: ScrollBehavior) => {
    stickToBottomRef.current = true;
    setShowJumpToLatest(false);
    setHasUnseenNewer(false);
    bottomRef.current?.scrollIntoView({ behavior });
  }, []);

  useLayoutEffect(() => {
    const el = containerRef.current;
    const prev = prevMessagesRef.current;
    prevMessagesRef.current = messages;
    if (!el) return;
    const pending = pendingRestoreRef.current;
    if (pending !== null) {
      el.scrollTop = el.scrollHeight - pending;
      pendingRestoreRef.current = null;
      return;
    }

    // A different conversation (no shared message ids) always opens at the newest message.
    const prevIds = new Set(prev.map((m) => m.id));
    const isNewThread = !messages.some((m) => prevIds.has(m.id));
    const last = messages[messages.length - 1];
    const prevLast = prev[prev.length - 1];
    const hasNewLast = !!last && last.id !== prevLast?.id;
    // The user's own message always brings them to the bottom.
    const sentByUser = hasNewLast && !isNewThread && last.direction === 'outbound';

    if (isNewThread || sentByUser || stickToBottomRef.current) {
      scrollToBottom('auto');
      return;
    }
    if (hasNewLast) {
      setHasUnseenNewer(true);
    }
  }, [messages, typingIndicator, scrollToBottom]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = isNearBottom(el);
    stickToBottomRef.current = atBottom;
    setShowJumpToLatest(!atBottom);
    if (atBottom) setHasUnseenNewer(false);
  };

  const handleLoadOlder = () => {
    const el = containerRef.current;
    if (el) {
      pendingRestoreRef.current = el.scrollHeight;
    }
    onLoadOlder?.();
  };

  // Group messages by date
  const groupedMessages: { date: string; messages: Message[] }[] = [];
  let currentDate = '';

  messages.forEach((msg) => {
    const msgDate = formatMessageDate(msg.sentAt, t);
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groupedMessages.push({ date: msgDate, messages: [msg] });
    } else {
      groupedMessages[groupedMessages.length - 1]?.messages.push(msg);
    }
  });

  return (
    <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        data-testid="message-thread-scroll"
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          backgroundColor: 'background.default',
        }}
      >
        {hasOlder && onLoadOlder && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Button
              size="small"
              onClick={handleLoadOlder}
              disabled={loadingOlder}
              startIcon={loadingOlder ? <CircularProgress size={14} /> : undefined}
            >
              {t('loadOlderMessages')}
            </Button>
          </Box>
        )}
        {groupedMessages.map((group) => (
          <Box key={group.date}>
            {/* Date separator */}
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: 'action.hover',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  color: 'text.secondary',
                }}
              >
                {group.date}
              </Typography>
            </Box>

            {/* Messages */}
            {group.messages.map((message) => {
              const isOutbound = message.direction === 'outbound';

              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isOutbound ? 'flex-end' : 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 1,
                      maxWidth: '75%',
                      flexDirection: isOutbound ? 'row-reverse' : 'row',
                    }}
                  >
                    {!isOutbound && (
                      <UserAvatar
                        name={customerName}
                        size={28}
                        variant="rounded"
                        sx={{ mb: 0.5 }}
                      />
                    )}

                    <Paper
                      elevation={0}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: isOutbound ? 'primary.main' : 'background.paper',
                        color: isOutbound ? 'primary.contrastText' : 'text.primary',
                        borderTopRightRadius: isOutbound ? 4 : 16,
                        borderTopLeftRadius: isOutbound ? 16 : 4,
                        border: isOutbound ? 'none' : 1,
                        borderColor: 'divider',
                        ...(message.visibility === 'internal'
                          ? {
                              backgroundColor: 'action.hover',
                              color: 'text.primary',
                              border: 1,
                              borderStyle: 'dashed',
                              borderColor: 'divider',
                            }
                          : {}),
                      }}
                    >
                      {message.visibility === 'internal' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {t('internalNote')}
                        </Typography>
                      )}
                      {message.attachments?.map((attachment) =>
                        attachment.type === 'image' ? (
                          <Box
                            key={attachment.id}
                            component="img"
                            src={attachment.url}
                            alt={attachment.fileName ?? t('attachImage')}
                            sx={{
                              display: 'block',
                              maxWidth: '100%',
                              maxHeight: 240,
                              borderRadius: 1,
                              mb: message.content ? 1 : 0,
                            }}
                          />
                        ) : (
                          <Link
                            key={attachment.id}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              mb: message.content ? 1 : 0,
                              color: isOutbound ? 'inherit' : 'primary.main',
                            }}
                          >
                            <InsertDriveFileOutlinedIcon fontSize="small" />
                            <Typography variant="body2">
                              {attachment.fileName ?? t('attachDocument')}
                            </Typography>
                          </Link>
                        ),
                      )}
                      {message.content &&
                      !message.attachments?.some((attachment) => attachment.fileName === message.content) ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.content}
                        </Typography>
                      ) : null}

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: isOutbound ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                            fontSize: '0.65rem',
                          }}
                        >
                          {formatMessageTime(message.sentAt)}
                        </Typography>
                        {isOutbound && <MessageStatusIcon status={message.status} errorReason={message.errorReason} />}
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}

        {/* Typing indicator */}
        {typingIndicator && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 1,
              }}
            >
              <UserAvatar
                name={typingIndicator.customerName}
                size={28}
                variant="rounded"
                sx={{ mb: 0.5 }}
              />

              <Paper
                elevation={0}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  borderTopLeftRadius: 4,
                  border: 1,
                  borderColor: 'divider',
                  minWidth: 50,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                      animation: 'typing 1.4s infinite',
                      '@keyframes typing': {
                        '0%, 60%, 100%': { opacity: 0.3 },
                        '30%': { opacity: 1 },
                      },
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                      animation: 'typing 1.4s infinite 0.2s',
                      '@keyframes typing': {
                        '0%, 60%, 100%': { opacity: 0.3 },
                        '30%': { opacity: 1 },
                      },
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                      animation: 'typing 1.4s infinite 0.4s',
                      '@keyframes typing': {
                        '0%, 60%, 100%': { opacity: 0.3 },
                        '30%': { opacity: 1 },
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          </Box>
        )}

        <div ref={bottomRef} />
      </Box>
      {showJumpToLatest && (
        <Fab
          size="small"
          color={hasUnseenNewer ? 'primary' : 'default'}
          onClick={() => scrollToBottom('smooth')}
          aria-label={hasUnseenNewer ? t('jumpToLatestNew') : t('jumpToLatest')}
          title={hasUnseenNewer ? t('jumpToLatestNew') : t('jumpToLatest')}
          sx={{ position: 'absolute', right: 16, bottom: 16, zIndex: 1 }}
        >
          <Badge color="error" variant="dot" invisible={!hasUnseenNewer}>
            <KeyboardArrowDownIcon />
          </Badge>
        </Fab>
      )}
    </Box>
  );
}
